/**
 * La logica dell'editor dei piani di discovery.
 *
 * ## Perché sta qui e non dentro il componente
 *
 * In questo progetto è già successo: la catena dei filtri della topologia stava
 * dentro il componente, e lo stesso difetto è sopravvissuto a **due** giri di
 * correzioni — là dentro non era verificabile da nessuna parte, e l'unico modo
 * di sapere se funzionava era aprire la pagina e fidarsi.
 *
 * Qui il rischio è lo stesso e il difetto è peggiore. L'editor deve offrire, per
 * ogni passo, **solo i fatti che a quel punto esistono già**. Se ne offre uno
 * prodotto da un passo successivo, chi lo sceglie scrive una condizione sempre
 * falsa: il passo non parte mai, la scansione riesce, e la conclusione è che
 * quegli apparati non hanno quel dato.
 *
 * Il Control Plane lo rifiuterebbe al salvataggio. Ma un editor che propone
 * qualcosa e poi lo rifiuta è un editor che fa perdere tempo per un errore che
 * poteva non far commettere.
 *
 * ## Cosa NON fa questo modulo
 *
 * **Non valida.** Il verdetto è del Control Plane
 * (`app/services/discovery/plan.py`), e riprodurlo qui significherebbe averne
 * due versioni che divergono — con l'editor che dice «va bene» e il salvataggio
 * che rifiuta, oppure il contrario, che è peggio.
 *
 * Questo modulo decide **cosa si può offrire**. Il Control Plane decide **se è
 * giusto**. Sono due domande diverse e stanno in due posti diversi.
 */

// --------------------------------------------------------------- il catalogo

export type PlanFact = {
  name: string
  type: 'bool' | 'number' | 'string' | 'list_number' | 'list_string'
  label: string
  hint?: string
}

/**
 * Un parametro che un passo accetta, **descritto** dal Control Plane.
 *
 * Senza descrizione l'editor non ha di meglio da offrire di una casella di
 * JSON: per limitare la scansione a due porte bisogna scrivere
 * `{"ports": [22, 443]}` a mano, con le parentesi giuste. Un JSON sbagliato
 * non dà errore al salvataggio — dà un passo che in campo fa altro.
 */
export type PlanParam = {
  name: string
  label: string
  /** Come si **scrive**, non come viaggia: `ports` è una lista di numeri e si
   *  scrive «22, 443, 8080», che è il modo in cui le porte si dicono a voce. */
  kind: 'int_list' | 'int' | 'ms' | 'string' | 'string_list' | 'bool'
  hint?: string
  /** Cosa fa la sonda se il campo resta vuoto. Va mostrato: «vuoto» e «zero»
   *  sono due cose diverse. */
  default?: string
}

export type PlanStepKind = {
  key: string
  label: string
  hint: string
  capability: string
  feature: string
  integration: string
  cost: 'basso' | 'medio' | 'alto'
  credential_kinds: string[]
  requires_credential: boolean
  produces: PlanFact[]
  /** Assente su un passo che non ne dichiara: l'editor ricade sul JSON. */
  params?: PlanParam[]
}

export type StepCatalogue = {
  steps: PlanStepKind[]
  operators: string[]
  combinators: string[]
  step_statuses: string[]
  max_steps: number
}

// ------------------------------------------------------------------ il piano

export type Condition = Record<string, unknown>

export type PlanStep = {
  id: string
  probe: string
  when?: Condition | null
  params?: Record<string, unknown>
  credential_refs?: string[]
  continue_on_failure?: boolean
}

/** Gli operatori applicabili a un fatto, **per il suo tipo**.
 *
 * Offrire `contains` su un booleano o `greater_than` su una stringa produrrebbe
 * una condizione che il Control Plane rifiuta — ed è un rifiuto evitabile,
 * perché il tipo del fatto lo si conosce nel momento in cui lo si sceglie.
 */
import { t } from './i18n/lingua'

export function operatorsFor(fact: PlanFact): { value: string; label: string }[] {
  const common = [{ value: 'exists', label: t('è presente') }]
  switch (fact.type) {
    case 'bool':
      return [
        { value: 'equals', label: t('è') },
        { value: 'not_equals', label: t('non è') },
        ...common,
      ]
    case 'number':
      return [
        { value: 'equals', label: t('è uguale a') },
        { value: 'not_equals', label: t('è diverso da') },
        { value: 'greater_than', label: t('è maggiore di') },
        { value: 'less_than', label: t('è minore di') },
        ...common,
      ]
    case 'string':
      return [
        { value: 'equals', label: t('è') },
        { value: 'not_equals', label: t('non è') },
        ...common,
      ]
    case 'list_number':
    case 'list_string':
      // Su un elenco si chiede se **contiene**. `equals` confronterebbe l'elenco
      // intero, che non è quasi mai ciò che si intende: il Control Plane lo
      // accetta con un avviso, e offrirlo qui inviterebbe a scriverlo.
      return [{ value: 'contains', label: t('contiene') }, ...common]
  }
}

/**
 * I fatti disponibili **prima** di un passo, cioè quelli su cui può condizionare.
 *
 * È il cuore del modulo. `position` è l'indice del passo nel piano: i fatti
 * arrivano dai passi che vengono prima, non da quelli che vengono dopo.
 *
 * Un fatto prodotto da un passo successivo non esiste ancora quando la
 * condizione viene valutata, quindi la condizione è sempre falsa e il passo non
 * parte mai — senza nessun errore, con la scansione che riesce.
 */
export function factsAvailableAt(
  steps: PlanStep[],
  position: number,
  catalogue: StepCatalogue,
): PlanFact[] {
  const byKey = new Map(catalogue.steps.map((kind) => [kind.key, kind]))
  const found = new Map<string, PlanFact>()
  for (let i = 0; i < position && i < steps.length; i++) {
    const kind = byKey.get(steps[i].probe)
    if (!kind) continue
    for (const fact of kind.produces) found.set(fact.name, fact)
  }
  return [...found.values()]
}

/**
 * Gli identificativi dei passi **precedenti**, per condizionare sul loro esito.
 *
 * Se stesso escluso: il proprio esito non esiste ancora quando si decide se
 * eseguirlo, ed è un errore che si commette naturalmente scegliendo da un elenco
 * che lo contiene.
 */
export function stepsAvailableAt(steps: PlanStep[], position: number): PlanStep[] {
  return steps.slice(0, Math.max(0, Math.min(position, steps.length)))
}

// ------------------------------------------------------ spostare e cancellare

export type MoveResult = {
  steps: PlanStep[]
  /**
   * I passi le cui condizioni sono diventate irraggiungibili per via dello
   * spostamento, con il motivo. Non si correggono da soli: indovinare cosa
   * intendeva chi ha scritto il piano è peggio che dirglielo.
   */
  broken: { stepId: string; reason: string }[]
}

/** Sposta un passo di una posizione, e dice cosa ha rotto. */
export function moveStep(
  steps: PlanStep[],
  index: number,
  direction: -1 | 1,
  catalogue: StepCatalogue,
): MoveResult {
  const target = index + direction
  if (index < 0 || index >= steps.length || target < 0 || target >= steps.length) {
    return { steps, broken: [] }
  }
  const moved = [...steps]
  ;[moved[index], moved[target]] = [moved[target], moved[index]]
  return { steps: moved, broken: brokenConditions(moved, catalogue) }
}

/** Toglie un passo, e dice quali condizioni restano senza il fatto che citavano. */
export function removeStep(
  steps: PlanStep[],
  index: number,
  catalogue: StepCatalogue,
): MoveResult {
  const remaining = steps.filter((_, i) => i !== index)
  return { steps: remaining, broken: brokenConditions(remaining, catalogue) }
}

/**
 * Le condizioni che, com'è il piano adesso, non si avvererebbero mai.
 *
 * Ricalcolate sull'intero piano dopo ogni modifica invece che sul solo passo
 * toccato: spostare un passo può rompere la condizione di un passo che non è
 * stato toccato, ed è precisamente il caso che non si vede guardando ciò che si
 * è appena mosso.
 *
 * Serve il catalogo, perché senza non si può sapere quali fatti produce un
 * passo — e una versione che tira a indovinare segnalerebbe come rotto tutto il
 * piano, cioè darebbe un avviso che si impara a ignorare.
 */
export function brokenConditions(
  steps: PlanStep[],
  catalogue: StepCatalogue,
): { stepId: string; reason: string }[] {
  const byKey = new Map(catalogue.steps.map((kind) => [kind.key, kind]))
  const broken: { stepId: string; reason: string }[] = []
  const available = new Set<string>()
  const ids = new Set<string>()

  for (const step of steps) {
    for (const { fact, stepRef } of referencedNames(step.when)) {
      if (fact && !available.has(fact)) {
        broken.push({
          stepId: step.id,
          reason: t(
            'la condizione usa «{fatto}», che a questo punto del piano non esiste ancora: il passo non partirebbe mai',
            { fatto: fact },
          ),
        })
      }
      if (stepRef && !ids.has(stepRef)) {
        broken.push({
          stepId: step.id,
          reason:
            stepRef === step.id
              ? t('la condizione guarda l’esito di se stesso, che non esiste ancora')
              : t('la condizione guarda l’esito di «{passo}», che viene dopo o non c’è', {
                  passo: stepRef,
                }),
        })
      }
    }
    const kind = byKey.get(step.probe)
    if (kind) for (const fact of kind.produces) available.add(fact.name)
    ids.add(step.id)
  }
  return broken
}

/** I nomi di fatto e di passo citati in una condizione, a qualunque profondità. */
export function referencedNames(
  condition: unknown,
): { fact?: string; stepRef?: string }[] {
  if (!condition || typeof condition !== 'object') return []
  const entry = condition as Record<string, unknown>

  for (const combinator of ['all', 'any'] as const) {
    if (combinator in entry) {
      const inner = entry[combinator]
      if (!Array.isArray(inner)) return []
      return inner.flatMap((child) => referencedNames(child))
    }
  }
  if ('not' in entry) return referencedNames(entry.not)
  if (typeof entry.fact === 'string') return [{ fact: entry.fact }]
  if (typeof entry.step === 'string') return [{ stepRef: entry.step }]
  return []
}

// ------------------------------------------------------------- leggibilità

/** Come si legge una condizione, in italiano. */
export function describeCondition(condition: unknown, facts: PlanFact[]): string {
  if (!condition || typeof condition !== 'object') return t('parte sempre')
  const entry = condition as Record<string, unknown>

  if (Array.isArray(entry.all)) {
    return entry.all.map((child) => describeCondition(child, facts)).join(t(' e '))
  }
  if (Array.isArray(entry.any)) {
    return entry.any.map((child) => describeCondition(child, facts)).join(t(' oppure '))
  }
  if ('not' in entry) return t('non ({condizione})', { condizione: describeCondition(entry.not, facts) })

  if (typeof entry.step === 'string') {
    return entry.status === 'ok'
      ? t('«{passo}» è riuscito', { passo: entry.step })
      : entry.status === 'failed'
        ? t('«{passo}» è fallito', { passo: entry.step })
        : t('«{passo}» è stato saltato', { passo: entry.step })
  }

  if (typeof entry.fact === 'string') {
    const fact = facts.find((f) => f.name === entry.fact)
    const label = fact?.label ?? entry.fact
    if ('exists' in entry) return entry.exists
        ? t('{campo} è presente', { campo: label })
        : t('{campo} non è presente', { campo: label })
    if ('equals' in entry) return formatValue(label, entry.equals, false)
    if ('not_equals' in entry) return formatValue(label, entry.not_equals, true)
    if ('contains' in entry)
      return t('{campo} contiene {valore}', { campo: label, valore: String(entry.contains) })
    if ('greater_than' in entry)
      return t('{campo} è maggiore di {valore}', {
        campo: label,
        valore: String(entry.greater_than),
      })
    if ('less_than' in entry)
      return t('{campo} è minore di {valore}', {
        campo: label,
        valore: String(entry.less_than),
      })
    return label
  }
  return t('condizione non leggibile')
}

/** «campo è valore» o «campo non è valore», come frase intera: il verbo e il
 *  valore non si traducono a pezzi. */
function formatValue(campo: string, value: unknown, negato: boolean): string {
  if (value === true) return negato ? t('{campo} non è vero', { campo }) : t('{campo} è vero', { campo })
  if (value === false) return negato ? t('{campo} non è falso', { campo }) : t('{campo} è falso', { campo })
  return negato
    ? t('{campo} non è {valore}', { campo, valore: String(value) })
    : t('{campo} è {valore}', { campo, valore: String(value) })
}

// ---------------------------------------------------------- costruire i passi

/**
 * Un identificativo libero, derivato dal tipo di passo.
 *
 * Derivato e non casuale: `ping`, `ping-2`, `ping-3` si leggono in una
 * condizione, `s7f3a1` no — e le condizioni si scrivono citando questi nomi.
 */
export function freeStepId(steps: PlanStep[], probe: string): string {
  const taken = new Set(steps.map((step) => step.id))
  if (!taken.has(probe)) return probe
  for (let n = 2; n < 100; n++) {
    const candidate = `${probe}-${n}`
    if (!taken.has(candidate)) return candidate
  }
  return `${probe}-${taken.size + 1}`
}

/**
 * Il piano proposto quando se ne crea uno nuovo.
 *
 * Non è vuoto di proposito: un editor che si apre bianco costringe a sapere già
 * come si costruisce una discovery per cominciare. Questa sequenza è quella che
 * risponde bene sulla maggior parte delle reti, e da lì si toglie o si aggiunge.
 *
 * L'ordine è quello che costa meno: il ping filtra, la scansione porte decide
 * cosa ha senso provare, e i passi cari partono solo dove c'è il servizio.
 */
export function suggestedPlan(catalogue: StepCatalogue): PlanStep[] {
  const has = (key: string) => catalogue.steps.some((step) => step.key === key)
  const plan: PlanStep[] = []

  if (has('ping')) plan.push({ id: 'vivo', probe: 'ping' })
  if (has('dns')) {
    plan.push({ id: 'nome', probe: 'dns', when: { fact: 'ping.alive', equals: true } })
  }
  if (has('tcp_scan')) {
    plan.push({ id: 'porte', probe: 'tcp_scan', when: { fact: 'ping.alive', equals: true } })
  }
  if (has('snmp')) {
    plan.push({
      id: 'rete',
      probe: 'snmp',
      when: { fact: 'tcp.open', contains: 161 },
      credential_refs: [],
    })
  }
  if (has('ssh')) {
    plan.push({
      id: 'unix',
      probe: 'ssh',
      when: {
        all: [
          { fact: 'tcp.open', contains: 22 },
          { fact: 'snmp.ok', equals: false },
        ],
      },
      credential_refs: [],
    })
  }
  if (has('winrm')) {
    plan.push({
      id: 'windows',
      probe: 'winrm',
      when: { fact: 'tcp.open', contains: 5985 },
      credential_refs: [],
    })
  }
  return plan
}

/**
 * I fatti che i passi **già nel piano** possono produrre in tutto.
 *
 * Serve a un avviso che nessun'altra parte può dare: un passo aggiunto in fondo
 * al piano non ha modo di essere condizionato su niente che venga dopo, e chi lo
 * aggiunge se ne accorge solo quando l'elenco dei fatti è vuoto.
 */
export function planProduces(steps: PlanStep[], catalogue: StepCatalogue): PlanFact[] {
  const byKey = new Map(catalogue.steps.map((kind) => [kind.key, kind]))
  const found = new Map<string, PlanFact>()
  for (const step of steps) {
    const kind = byKey.get(step.probe)
    if (!kind) continue
    for (const fact of kind.produces) found.set(fact.name, fact)
  }
  return [...found.values()]
}

/**
 * Se un passo costoso è messo davanti a tutto ciò che potrebbe filtrarlo.
 *
 * Lo stesso avviso che dà il Control Plane, calcolato qui perché serve **mentre**
 * si costruisce il piano: saperlo al salvataggio significa averlo già scritto.
 * È un avviso, non un divieto — su una subnet di soli server può essere la
 * scelta giusta, e chi la fa sa perché.
 */
export function expensiveWithoutFilter(
  steps: PlanStep[],
  catalogue: StepCatalogue,
): string[] {
  const byKey = new Map(catalogue.steps.map((kind) => [kind.key, kind]))
  const filters = new Set(['ping', 'tcp_scan'])
  let seenFilter = false
  const warned: string[] = []

  for (const step of steps) {
    if (filters.has(step.probe)) {
      seenFilter = true
      continue
    }
    const kind = byKey.get(step.probe)
    if (!kind) continue
    if (kind.cost === 'alto' && !seenFilter && !step.when) warned.push(step.id)
  }
  return warned
}


// ------------------------------------------------ i parametri, come si scrivono

/**
 * Da valore a testo: quello che si vede nella casella.
 *
 * Una lista diventa «22, 443», non «[22,443]»: la casella deve somigliare a
 * come si dicono le porte, altrimenti tanto valeva lasciare il JSON.
 */
export function paramToText(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

/**
 * Da testo a valore, con il verdetto.
 *
 * Restituisce `undefined` quando il campo è **vuoto**, che significa «usa il
 * predefinito» e non «zero»: scrivere 0 al posto di niente farebbe una
 * scansione senza porte, o un'attesa nulla, cioè un passo che non trova niente
 * senza dare errore.
 *
 * Un valore non numerico non viene silenziosamente scartato: torna un errore,
 * perché «22, 44e, 80» digitato di fretta altrimenti diventerebbe una lista di
 * due porte e nessuno se ne accorgerebbe.
 */
export function paramFromText(
  kind: PlanParam['kind'],
  text: string,
): { value?: unknown; error?: string } {
  const trimmed = text.trim()
  if (!trimmed) return {}

  if (kind === 'int_list') {
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean)
    const numbers: number[] = []
    for (const part of parts) {
      if (!/^\d+$/.test(part)) return { error: t('«{valore}» non è un numero', { valore: part }) }
      const n = Number(part)
      if (n < 1 || n > 65535) return { error: t('«{valore}» non è una porta (1-65535)', { valore: part }) }
      numbers.push(n)
    }
    if (!numbers.length) return {}
    // Ordinate e senza ripetizioni: l'elenco finisce nel piano, e due piani
    // identici scritti in ordine diverso risulterebbero diversi.
    return { value: [...new Set(numbers)].sort((a, b) => a - b) }
  }

  if (kind === 'int' || kind === 'ms') {
    if (!/^\d+$/.test(trimmed)) return { error: t('serve un numero intero') }
    return { value: Number(trimmed) }
  }

  if (kind === 'bool') return { value: trimmed === 'true' || trimmed === 'sì' }

  if (kind === 'string_list') {
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean)
    return parts.length ? { value: parts } : {}
  }

  return { value: trimmed }
}
